import { compose, map } from 'lodash/fp';
import { record } from 'fp-ts';
import { createAction } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { createBackgroundAction } from '@mews-commander/utils';
import { fetchResources } from '@mews-commander/legacy-entities/src/actions/resources';
import {
    getEnterpriseBookableServiceIds,
    getTimeUnitByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { getResourceCategoriesByServiceIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import {
    getServiceId,
    getOrder,
    getNextReservationPlaceId,
    getSelectedEntityEnterpriseIds,
    getSelectedEntityTimeUnitServiceIds,
    hasTimesSelected,
} from '../selectors';
import { createDateRangeKey } from '../utils/keys';
import { getShowDateServiceDiscrepancyBanner } from '../selectors/utils';
import { fetchAvailability } from './api';
import { clearReservations } from './reservations';
import { preselectResourceCategoryType, showRates } from './screens';
import {
    CATEGORY_AVAILABILITY_CHANGED,
    RESERVATION_PLACE_SELECTED,
    RESERVATION_PLACE_CHANGE_CONFIRMED,
    RESERVATION_PLACE_CHANGE_CANCELLED,
    SERVICE_CATEGORIES_FETCH,
} from './types';

export const changeCategoryAvailability =
    ({ categoryId, selectedCount }: $TSFixMe, scope: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) =>
        dispatch(
            createAction(
                CATEGORY_AVAILABILITY_CHANGED,
                {
                    serviceId: getServiceId(getState(), { scope }),
                    dateRangeKey: compose(createDateRangeKey, getOrder)(getState(), { scope }),
                    categoryId,
                    selectedCount,
                },
                { scope }
            )
        );

export const confirmReservationPlaceChange = (scope: $TSFixMe) => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState();
    const meta = { scope };

    const reservationPlaceId = getNextReservationPlaceId(state, meta);

    dispatch(clearReservations(scope));
    dispatch(createAction(RESERVATION_PLACE_CHANGE_CONFIRMED, undefined, meta));
    dispatch(createAction(RESERVATION_PLACE_SELECTED, reservationPlaceId, { scope }));
    dispatch(processSelectedReservationPlace(scope));
};

export const cancelReservationPlaceChange = (scope: $TSFixMe) =>
    createAction(RESERVATION_PLACE_CHANGE_CANCELLED, undefined, { scope });

const RESOURCE_CATEGORIES_EXTENT = {
    Slots: false,
    Resources: false,
    ResourceFeatures: false,
    ResourceCategories: true,
    ResourceCategoryAssignments: true,
    ResourceFeatureAssignments: false,
};

export const fetchServicesCategories =
    (enterpriseIds: string[]): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        const state = getState();
        const promise = Promise.all(
            map((enterpriseId) => {
                // @ts-expect-error
                const serviceIds: string[] = getEnterpriseBookableServiceIds(state, { enterpriseId });
                const resourceCategories = getResourceCategoriesByServiceIds(state, { serviceIds });
                return (
                    record.isEmpty(resourceCategories) &&
                    dispatch(fetchResources({ enterpriseId, extent: RESOURCE_CATEGORIES_EXTENT }))
                );
            })(enterpriseIds)
        );
        return dispatch(createBackgroundAction(SERVICE_CATEGORIES_FETCH, promise));
    };

export const processSelectedReservationPlace =
    (scope: string): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const state = getState();
        const selectedServiceId = getServiceId(state, { scope });
        const serviceIds = selectedServiceId
            ? [selectedServiceId]
            : getSelectedEntityTimeUnitServiceIds(state, { scope });

        if (selectedServiceId && getShowDateServiceDiscrepancyBanner(state, { scope, serviceId: selectedServiceId })) {
            return Promise.resolve();
        }

        const enterpriseIds = getSelectedEntityEnterpriseIds(state, { scope });
        return dispatch(fetchServicesCategories(enterpriseIds)).then(() => {
            const timeUnit = getTimeUnitByServiceId(getState(), { serviceId: selectedServiceId });
            if (selectedServiceId && timeUnit !== TimeUnit.Hour) {
                dispatch(showRates(scope));
            }
            dispatch(preselectResourceCategoryType({ serviceIds, scope }));
            serviceIds.forEach(async (serviceId) => {
                const isHourlyService = getTimeUnitByServiceId(state, { serviceId }) === TimeUnit.Hour;
                const timesSelected = hasTimesSelected(state, { scope });
                if (!isHourlyService || timesSelected) {
                    await dispatch(fetchAvailability(serviceId, scope));
                }
            });
        });
    };
